import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Generic sortable list hook using HTML5 drag-and-drop.
 *
 * @template T
 * @param {T[]} items - Source items
 * @param {{
 *   getId?: (item: T) => string | number,
 *   onReorder?: (newItems: T[]) => void,
 *   disabled?: boolean,
 * }} [options]
 * @returns {{
 *   orderedItems: T[];
 *   draggingId: string | number | null;
 *   destinationIndex: number | null;
 *   onDragStart: (e: DragEvent | any, item: T) => void;
 *   onDragEnter: (e: DragEvent | any, item: T) => void;
 *   onDrop: (e?: DragEvent | any, item?: T) => void;
 *   onDragOver: (e: DragEvent | any) => void;
 *   onDragEnd: (e: DragEvent | any) => void;
 * }}
 */
export default function useSortableList(items, options = {}) {
  const { getId, onReorder, disabled = false } = options;

  const getItemId = useMemo(() => {
    if (typeof getId === 'function') return getId;
    return (item) => (item && item.id !== undefined ? item.id : item);
  }, [getId]);

  const [orderedItems, setOrderedItems] = useState(items ?? []);
  const [draggingId, setDraggingId] = useState(null);
  const [destinationIndex, setDestinationIndex] = useState(null);

  // Keep internal state in sync with external items when not disabled
  useEffect(() => {
    if (!disabled) setOrderedItems(items ?? []);
  }, [items, disabled]);

  const onDragStart = useCallback((e, item) => {
    const id = getItemId(item);
    setDraggingId(id);
    setDestinationIndex((prev) => {
      const idx = (orderedItems ?? []).findIndex((i) => getItemId(i) === id);
      return idx >= 0 ? idx : prev;
    });
  }, [getItemId, orderedItems]);

  const onDragEnter = useCallback((e, targetItem) => {
    if (draggingId === null) return;

    setOrderedItems((prev) => {
      const currentIndex = prev.findIndex((i) => getItemId(i) === draggingId);
      const targetIndex = prev.findIndex((i) => getItemId(i) === getItemId(targetItem));
      if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) return prev;

      setDestinationIndex(targetIndex);

      const next = prev.slice();
      const [dragged] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, dragged);
      return next;
    });
  }, [draggingId, getItemId]);

  const onDrop = useCallback((e) => {
    if (draggingId === null || (orderedItems?.length ?? 0) <= 1) return;
    setDraggingId(null);
    setDestinationIndex(null);
    if (typeof onReorder === 'function') onReorder(orderedItems);
  }, [draggingId, orderedItems, onReorder]);

  const onDragOver = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
  }, []);

  const onDragEnd = useCallback((e) => {
    setDraggingId(null);
    setDestinationIndex(null);
    if (e?.preventDefault) e.preventDefault();
  }, []);

  return {
    orderedItems,
    draggingId,
    destinationIndex,
    onDragStart,
    onDragEnter,
    onDrop,
    onDragOver,
    onDragEnd,
  };
}

